import { defineNuxtPlugin } from 'nuxt/app'
import type { FetchResponse } from 'ofetch'
import type {
  GraphqlMiddlewareResponseUnion,
  GraphqlResponse,
} from '#nuxt-graphql-middleware/response'
import { useNotifications } from '~/stores/notifications'

interface MessengerMessage {
  type: 'status' | 'warning' | 'error'
  message: string
}

type PossibleResponse = FetchResponse<
  GraphqlResponse<GraphqlMiddlewareResponseUnion>
>

/**
 * Try to extract the messages from a GraphQL query or mutation.
 */
function extractMessages(response?: PossibleResponse): MessengerMessage[] {
  if (
    response &&
    response._data &&
    typeof response._data === 'object' &&
    'messengerMessages' in response._data.data
  ) {
    const messages = response._data.data.messengerMessages
    delete response._data.data.messengerMessages
    return messages
  }

  return []
}

/**
 * This is only called when performing a query or mutation from within the nuxt
 * app (e.g. not via custom server routes).
 */
export default defineNuxtPlugin({
  name: 'phs-graphql-fetch-options',
  dependsOn: ['nuxt-graphql-middleware-provide-state'],
  setup(app) {
    const state = useGraphqlState()
    const notify = useNotifications()

    if (!state) {
      return
    }

    state.fetchOptions = {
      onResponse(result) {
        try {
          extractMessages(result.response).forEach((v) => {
            // Set response uncacheable when there's messages.
            useRouteCache((c) => c.setUncacheable(), app.ssrContext?.event)
            if (v.type === 'error') {
              notify.error(v.message)
            } else {
              notify.success(v.message)
            }
          })
          if (result.response?._data?.__cacheability) {
            const cacheability = result.response._data.__cacheability
            if (cacheability.isCacheable) {
              useRouteCache(
                (c) => c.addTags(cacheability.tags).setCacheable(),
                app.ssrContext?.event,
              )
            } else {
              useRouteCache((c) => c.setUncacheable(), app.ssrContext?.event)
            }
            delete result.response._data.__cacheability
          }
        } catch {
          useRouteCache((c) => c.setUncacheable(), app.ssrContext?.event)
          // Something went wrong. Make the route uncacheable to prevent caching errors.
        }
      },
      onRequest({ options }: any) {
        if (import.meta.server) {
          try {
            if (!options.headers) {
              options.headers = {}
            }

            const headers = useRequestHeaders()
            for (const [key, value] of Object.entries(headers)) {
              options.headers.set(key, value)
            }

            return options
          } catch (e) {
            console.error('Exception in onRequest handler:', e)
          }
        }
      },
    }
  },
})
